import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { useCreateStore } from '@/entities/create';
import { CreateSetDataStep } from '@/widgets/create-steps/CreateSetDataStep';
import { CreateSetErrorModal } from '@/widgets/create-steps/CreateSetErrorModal';

const normalizeSearchParam = (value: unknown) => {
    if (typeof value !== 'string') return value;

    const trimmedValue = value.trim();
    const quote = trimmedValue[0];
    const isQuoted =
        (quote === '"' || quote === "'") && trimmedValue[trimmedValue.length - 1] === quote;

    return isQuoted ? trimmedValue.slice(1, -1).trim() : trimmedValue;
};

const positiveIntegerSearchParam = z.preprocess(
    normalizeSearchParam,
    z.coerce.number().int().positive()
);

const createSetSearchSchema = z.object({
    curriBookCode: positiveIntegerSearchParam,
    setId: positiveIntegerSearchParam.transform(String),
});

type ValidCreateSetSearch = z.infer<typeof createSetSearchSchema>;
type CreateSetSearch = Partial<ValidCreateSetSearch>;

export const Route = createFileRoute('/create-set/')({
    validateSearch: (search): CreateSetSearch => {
        const result = createSetSearchSchema.safeParse(search);
        return result.success ? result.data : {};
    },
    component: CreateSetPage,
});

function CreateSetPage() {
    const { curriBookCode, setId } = Route.useSearch();

    if (!curriBookCode || !setId) {
        return <CreateSetErrorModal reason="invalid-params" />;
    }

    return <ValidCreateSetPage curriBookCode={curriBookCode} setId={setId} />;
}

function ValidCreateSetPage({ curriBookCode, setId }: ValidCreateSetSearch) {
    const setState = useCreateStore((state) => state.setState);

    useEffect(() => {
        setState('type', 'data');
        setState('currentStep', 3);
        setState('setList', []);
    }, [curriBookCode, setState]);

    return <CreateSetDataStep curriBookCode={curriBookCode} setId={setId} />;
}
